import { useMutation, useQueryClient } from '@tanstack/react-query';
import { plansApi } from '../api/plans.api';
import type { CreatePlanRequest, UpdatePlanDetailsRequest } from '../types/plans.types';
import { planKeys } from './queryKeys';

export const useCreatePlan = () => {
  const queryClient = useQueryClient();

  const createPlan = useMutation({
    mutationFn: (data: CreatePlanRequest) => plansApi.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
    },
  });

  return {
    createPlan: createPlan.mutate,
    isCreating: createPlan.isPending,
  };
};

export const useRetryPlanSync = (planId: string) => {
  const queryClient = useQueryClient();

  const retrySync = useMutation({
    mutationFn: () => plansApi.retryPlanSync(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planKeys.detail(planId) });
    },
  });

  return {
    retrySync: retrySync.mutate,
    isRetrying: retrySync.isPending,
  };
};

export const useTogglePlanStatus = () => {
  const queryClient = useQueryClient();

  const toggleStatus = useMutation({
    mutationFn: ({ planId, isActive }: { planId: string; isActive: boolean }) =>
      plansApi.togglePlanStatus(planId, isActive),
    onSuccess: (_data, { planId }) => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planKeys.detail(planId) });
    },
  });

  return {
    toggleStatus: toggleStatus.mutate,
    isToggling: toggleStatus.isPending,
  };
};

export const useUpdatePlanDetails = (planId: string) => {
  const queryClient = useQueryClient();

  const updatePlanDetails = useMutation({
    mutationFn: (data: UpdatePlanDetailsRequest) => plansApi.updatePlanDetails(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planKeys.detail(planId) });
    },
  });

  return {
    updatePlanDetails: updatePlanDetails.mutate,
    isUpdating: updatePlanDetails.isPending,
  };
};
